import { HStack } from "@chakra-ui/react"
import Checkbox from "@pathwright/ui/src/components/form/form-checkbox/Checkbox"
import RichTextInput from "@pathwright/ui/src/components/form/form-richtext/RichTextInput"
import { StyledFieldMinimal } from "@pathwright/ui/src/components/form/form-utils/FieldWrapper"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import LoadingBlock from "@pathwright/ui/src/components/loading/LoadingBlock"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import DiscardButton from "@pathwright/ux/src/components/form/DiscardButton"
import SubmitButton from "@pathwright/ux/src/components/form/SubmitButton"
import { FormikWithDiscardFormState } from "@pathwright/ux/src/components/form/state/FormikDiscardFormState"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import { Form, FormikProps } from "formik"
import { useEffect, useState } from "react"
import styled from "styled-components"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"
import { CompletionsItemFragment } from "../../../api/generated"
import KeyboardFormSubmit from "../../../components/formik/KeyboardFormSubmit"
import { useReviewContext } from "../../state/ReviewContextProvider"
import { useInboxReviewers } from "../Reviewers"
import useReviewNotify from "./useReviewNotify"

const ReviewCompletionScoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 1.2em;
  input {
    width: 60px;
  }
  .ScoreFieldRow {
    margin-bottom: 10px;
  }
  > div {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
`

const isNumeric = (n: any) => {
  return !isNaN(parseFloat(n)) && isFinite(n)
}

const isReviewed = (item: CompletionsItemFragment) => {
  return !!item.completion.checked_date
}

const ReviewCompletionScore = ({
  step,
  formik
}: {
  step: CompletionsItemFragment
  formik: FormikProps<FormValues>
}) => {
  const { t } = useTranslate()
  const completion = step.completion

  let [score, setScore] = useState(
    isReviewed(step) ? completion.userpoints_earned : undefined
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isNumeric(e.target.value)) {
      setScore(parseInt(e.target.value))
    } else {
      // Allows submitting an empty value as a 0.
      setScore(0)
    }
  }

  useEffect(() => {
    if (
      score !== step.completion.userpoints_earned ||
      score !== formik.values.userpoints_earned
    ) {
      formik.setFieldValue("userpoints_earned", score)
      formik.setFieldTouched("userpoints_earned", true)
    }
  }, [step, score])

  const { values, touched, errors, handleBlur } = formik

  if ((completion.userpoints_value || 0) <= 0) return null

  const minPassingScore = completion.minimum_passing_score

  const showNotPassedMessage =
    typeof minPassingScore == "number" &&
    isNumeric(score) &&
    (score || 0) < minPassingScore &&
    touched.userpoints_earned

  const perc =
    isNumeric(score) && isNumeric(completion.userpoints_value)
      ? Math.round(((score || 0) / (completion.userpoints_value || 0)) * 100)
      : undefined

  return (
    <ReviewCompletionScoreContainer>
      <StyledFieldMinimal className="ScoreFieldRow">
        <input
          name="userpoints_earned"
          defaultValue={values.userpoints_earned}
          type="number"
          required
          onChange={handleChange}
          onBlur={handleBlur}
          tabIndex={0}
        />
        <b>
          {" "}
          /{" "}
          {t("{{ count }} pt", {
            defaultValue_plural: "{{ count }} pts",
            count: completion.userpoints_value
          })}
        </b>
        {perc !== undefined && <span>&nbsp;({perc}%)</span>}
      </StyledFieldMinimal>
      {showNotPassedMessage && (
        <span style={{ color: "red" }}>
          <Pathicon name="warning" />
          <em>
            This step is not passing. {step.completion.user?.display_name} needs
            at least {minPassingScore} points to pass this step.
          </em>
        </span>
      )}
    </ReviewCompletionScoreContainer>
  )
}

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  max-height: 100%;

  .ql-editor {
    /* Need a min height so that toolbar isn't clippedd by overflow hidden styles */
    min-height: 200px;
  }

  .notify {
    margin-top: 10px;
    display: flex;
    flex-direction: row;
    align-items: center;

    label {
      font-weight: normal;
    }
  }

  > footer {
    display: flex;
    align-items: center;
    margin-top: 10px;

    > span {
      display: inline-block;
      /* font-size: 13px; */
      color: #666;
      margin: 0 6px;
    }
  }
`

const FormValuesSchema = z.object({
  userpoints_explanation: z.string().optional().nullable(),
  userpoints_earned: z.number().min(0).optional(),
  notify: z.boolean().optional()
})

type FormValues = z.infer<typeof FormValuesSchema>

const validator = toFormikValidate(FormValuesSchema)

const ReviewCompletionForm = ({ onCancel }: { onCancel: () => void }) => {
  const { t } = useTranslate()
  const {
    selected,
    actions: { reviewSelected }
  } = useReviewContext()

  const step = selected!.data

  const { reviewers } = useInboxReviewers(selected!.data.path_id)

  let initialValues = FormValuesSchema.parse({
    userpoints_earned: step.completion.userpoints_earned,
    userpoints_explanation: step.completion.userpoints_explanation,
    notify: useReviewNotify()[0]
  })

  const isEditing =
    !!step.completion.checked_date && !step.completion.needs_grading

  const handleSubmit = (values: FormValues) =>
    reviewSelected({
      userpointsExplanation: values.userpoints_explanation || null,
      userpointsEarned: values.userpoints_earned || 0,
      notify: !!values.notify
    })

  const handleChangeExplanation = (
    formik: FormikProps<FormValues>,
    value: string | null
  ) => {
    // Cleaning empty userpoints_explanation value.
    // Doing this onChange prevents unwanted discrepancy with
    // form state storage if user deletes all content value.
    if (value === "<p><br></p>") {
      value = null
    }

    formik.setFieldValue("userpoints_explanation", value)
  }

  return (
    <FormikWithDiscardFormState
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validator}
      validateOnMount
      formStateKey={["review", step.id!]}
    >
      {(formik: FormikProps<FormValues>) => (
        <StyledForm style={{ width: "100%" }}>
          <KeyboardFormSubmit />
          <ReviewCompletionScore step={step} formik={formik} />
          <RichTextInput
            placeholder={"Review notes (optional)..."}
            value={formik.values.userpoints_explanation}
            onChange={(value: string | null) =>
              handleChangeExplanation(formik, value)
            }
            // Controlling to ensure form state storage is initially applied.
            controlled
            tabIndex={1}
            hideStatus
            hideLabel
            errors={null}
            stretch={true}
            LoadingComponent={() => <LoadingBlock style={{ height: "2em" }} />}
            style={{ width: "100%", overflow: "hidden auto" }}
          />

          <div className="notify">
            <Checkbox
              name={"notify"}
              checked={formik.values.notify}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                formik.setFieldValue("notify", e.target.checked)
              }
            />
            <Tooltip
              title={t(
                "Send {{ user }} an in-app and email notification with your review. (Reviewers will be copied on the email.)",
                {
                  user: step.completion.user?.first_name
                }
              )}
              popover={{
                openDelay: 300,
                placement: "top"
              }}
              portal={false}
            >
              <label htmlFor="notify">
                {t("Notify {{ user }}", {
                  user: step.completion.user?.first_name
                })}
              </label>
            </Tooltip>
          </div>
          {isEditing ? (
            <HStack as="footer">
              <SubmitButton
                colorScheme="brand"
                variant="solid"
                children="Update Review"
                flexGrow={1}
              />
              <span>or</span>
              <DiscardButton
                colorScheme="gray"
                variant="solid"
                children="cancel"
                onClick={onCancel}
                // Always enabled since canceling acts as both to discard any form state
                // AND to exit editing mode.
                isDisabled={false}
              />
            </HStack>
          ) : (
            <HStack as="footer">
              <SubmitButton
                colorScheme="brand"
                variant="solid"
                children="Complete Review"
                // Full width button when not displaying "for all reviewers" text.
                // You'd think we could do this with CSS only but alas, sx and _* options
                // not seeming to work here.
                w={(reviewers?.length || 0) > 1 ? "auto" : "100%"}
              />
              {(reviewers?.length || 0) > 1 && <span>for all reviewers</span>}
            </HStack>
          )}
        </StyledForm>
      )}
    </FormikWithDiscardFormState>
  )
}

export default ReviewCompletionForm
